import React from 'react';

import { classnames } from '@/utils/classnames.ts';
import mergeRefs from '@/utils/mergeRefs.ts';

export interface Slottable {
    asChild?: true;
}

type Props = Record<string, unknown>;

// The child's props win, except that handlers both run (the child's first), refs both receive the node and
// classNames and styles are combined.
const mergeProps = (slotProps: Props, childProps: Props) => {
    const merged = { ...slotProps, ...childProps };
    for (const name in childProps) {
        const slotValue = slotProps[name];
        const childValue = childProps[name];
        if (slotValue === undefined) continue;

        if (/^on[A-Z]/.test(name) && typeof slotValue === 'function' && typeof childValue === 'function') {
            merged[name] = (...args: unknown[]) => {
                childValue(...args);
                slotValue(...args);
            };
        } else if (name === 'ref') {
            merged[name] = mergeRefs(slotValue as React.Ref<unknown>, childValue as React.Ref<unknown>);
        } else if (name === 'style') {
            merged[name] = { ...(slotValue as object), ...(childValue as object) };
        } else if (name === 'className') {
            merged[name] = classnames(slotValue as string, childValue as string);
        }
    }
    return merged;
};

// Renders its single child with the props given to the slot merged in, so a styled component can lend its look to
// another element (`asChild`).
const Slot = ({ children, ...props }: Props & { children?: React.ReactNode }) => {
    if (!React.isValidElement<Props>(children)) {
        if (React.Children.count(children) > 1) throw new Error('Slot expects a single element as its child.');
        return null;
    }
    return React.cloneElement(children, mergeProps(props, children.props));
};

export default Slot;
